import React, {FC} from 'react';
import styled from 'styled-components';

import {TeammateAvailabilityStatusesEnum} from '../../../libs/shared/core/src/models/api/teammateModel';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {isDarkTheme} from '../../../libs/shared/ui/src/theme/themeTypeHelpers';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {saveUserAction} from '../../store/data/teammates/teammatesActions';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {computeTeammateAvailabilityValues} from './teammateAvailabilityHelpers';

/*
 * Styles.
 */

const AvailabilityStatusesDiv = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${fontSizes.medium};
`;

const AvailabilityStatusWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  padding: 7px 20px;
  justify-content: space-between;
  width: 100%;
  &:hover {
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

const AvailabilityStatusDiv = styled.div<{$isSelected?: boolean}>`
  display: flex;
  gap: 5px;
  align-items: center;
  font-weight: ${(p) => (p.$isSelected ? fontWeights.bold : fontWeights.normal)};
`;

const CircleBackgroundIcon = styled.div<{$color: string}>`
  border-radius: 50%;
  height: 20px;
  width: 20px;
  ${center()};
  background-color: ${(p) => p.$color};
  color: ${(p) => (isDarkTheme(p.theme) ? p.theme.alphas.black50 : p.theme.greys.white)};
  margin-right: 2px;
`;

/*
 * Component.
 */

export const TeammateAvailabilityPicker: FC = () => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const currentStatus = user.availabilityStatus;

  const onAvailabilityStatusChange = (status: TeammateAvailabilityStatusesEnum) => {
    dispatch(saveUserAction({availabilityStatus: status}));
  };

  const renderAvailabilityStatus = (status: TeammateAvailabilityStatusesEnum) => {
    const isSelected = status === currentStatus;
    const {color, icon, text, tooltip} = computeTeammateAvailabilityValues(status);

    return (
      <AvailabilityStatusWrapperDiv onClick={() => onAvailabilityStatusChange(status)}>
        <AvailabilityStatusDiv $isSelected={isSelected}>
          <CircleBackgroundIcon $color={theme.palette[color].shade40}>
            <Icon name={icon} />
          </CircleBackgroundIcon>
          {text}
          {tooltip && <TooltipCoordinator render={() => tooltip} placement="top" />}
        </AvailabilityStatusDiv>
        {isSelected && <Icon name="checkmarkSelectedSmall" color={theme.palette.blue.shade40} />}
      </AvailabilityStatusWrapperDiv>
    );
  };
  return (
    <AvailabilityStatusesDiv>
      {renderAvailabilityStatus(TeammateAvailabilityStatusesEnum.AVAILABLE)}
      {renderAvailabilityStatus(TeammateAvailabilityStatusesEnum.BUSY)}
      {renderAvailabilityStatus(TeammateAvailabilityStatusesEnum.OUT_OF_OFFICE)}
    </AvailabilityStatusesDiv>
  );
};
